import * as React from "react";
import { useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";
import { WindowChrome } from "./WindowChrome";
import { useMinimizedDock } from "./MinimizedDock";
import "./BrandWindow.scss";

export type DraggableWindowProps = React.PropsWithChildren<{
  title: string;
  className?: string;
}>;

/**
 * An in-flow brand window that can be dragged by its title bar, maximized, and
 * minimized to the dock. Used for the desktop Clues window.
 */
export const DraggableWindow: React.FC<DraggableWindowProps> = ({
  title,
  className,
  children,
}) => {
  const id = useId();
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [minimized, setMinimized] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const drag = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(
    null
  );
  const dock = useMinimizedDock();

  useEffect(() => {
    if (minimized) {
      dock.add({ id, title, restore: () => setMinimized(false) });
      return () => dock.remove(id);
    }
    dock.remove(id);
  }, [minimized, id, title, dock]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (fullscreen) return;
    drag.current = { sx: e.clientX, sy: e.clientY, bx: pos.x, by: pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPos({
      x: drag.current.bx + (e.clientX - drag.current.sx),
      y: drag.current.by + (e.clientY - drag.current.sy),
    });
  };
  const endDrag = (e: React.PointerEvent) => {
    drag.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* no-op */
    }
  };

  if (minimized) return null;

  return (
    <div
      className={clsx("brandWindow", { "brandWindow--fullscreen": fullscreen }, className)}
      style={fullscreen ? undefined : { transform: `translate(${pos.x}px, ${pos.y}px)` }}
    >
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <WindowChrome
          title={title}
          draggable={!fullscreen}
          fullscreen={fullscreen}
          onMinimize={() => setMinimized(true)}
          onToggleFullscreen={() => setFullscreen((f) => !f)}
          onClose={() => setMinimized(true)}
        />
      </div>
      <div className="brandWindow-body">{children}</div>
    </div>
  );
};
